// import type のため実行時の require は発生しない。useInvestmentStyleDiagnosisForm.ts の重い依存（capacitor 連携等）をここに引き込まないための分離。
import type { Answers } from "@/types/investmentStyleDiagnosis";

// タブを閉じたら消えれば十分なため localStorage ではなく sessionStorage を使う。
// リロードや直リンクでの再開のためだけの保存であり、複数タブ・複数セッションをまたいだ永続化は意図しない。
export const STORAGE_KEY = "investment_style_diagnosis_answers";

// 「このタブセッション中に診断を完了した」ことだけを覚える軽量フラグ用のキー。
// 恒久的な完了判定はサーバー側の is_investment_style_diagnosis_complete が情報源であり（src/WithAuth.tsx）、
// これは完了直後のブラウザバックでフォームへ逆戻りするのを防ぐためだけの、セッション内限定の補助フラグ。
export const COMPLETED_STORAGE_KEY = "investment_style_diagnosis_completed";

const getSessionStorage = (): Storage | null => {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    // Safari のプライベートブラウジング等でストレージアクセスが例外になるケースを握りつぶす
    return null;
  }
};

// 設問追加（14画面19問）で増えた単一選択。追加前のアプリで保存された回答には存在しない。
const ADDED_SINGLE_KEYS = [
  "target_monthly_income",
  "borrowing_stance",
  "rental_income_use",
  "work_outlook",
  "first_purchase_timing",
  "planner_comparison_count",
] as const;

const isNullableString = (x: unknown) => x === null || typeof x === "string";

// アプリ更新によるフィールド追加/変更や、ストレージ破損で欠けた・型が違うJSONが残っていた場合に
// isStepValid/buildRequest 側で answers.purposes.length 等が unsafe に落ちて白画面化するのを防ぐための
// 構造チェック。enum の値そのものまでは検証せず、配列/文字列/null 等の「形」だけを見る。
// 追加された設問は欠けていてもよく（未回答として補う）、あれば形だけ確認する。
const isValidAnswersShape = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    Array.isArray(v.purposes) &&
    isNullableString(v.primary_purpose) &&
    (v.ideal_text === undefined || typeof v.ideal_text === "string") &&
    ADDED_SINGLE_KEYS.every((key) => v[key] === undefined || isNullableString(v[key])) &&
    Array.isArray(v.interest) &&
    Array.isArray(v.success_conditions) &&
    isNullableString(v.monthly_out_of_pocket) &&
    isNullableString(v.holding_period) &&
    (v.location_yield_preference === null || typeof v.location_yield_preference === "number") &&
    isNullableString(v.management_style) &&
    isNullableString(v.short_term_sale_intent) &&
    Array.isArray(v.meeting_check_items) &&
    Array.isArray(v.explanation_styles) &&
    typeof v.meeting_free_text === "string" &&
    typeof v.anxiety === "object" &&
    v.anxiety !== null &&
    !Array.isArray(v.anxiety) &&
    Array.isArray(v.avoid_sales_styles)
  );
};

// 追加前のアプリで保存された回答は、追加された設問を未回答（null）として補う。
// 自由記述のない更新版の回答にも対応する。未回答の設問はフォームのガードでその画面へ戻される。
const normalizeAnswers = (v: Record<string, unknown>): Answers => {
  const rest = { ...v };
  rest.ideal_text = v.ideal_text ?? "";
  const added = Object.fromEntries(ADDED_SINGLE_KEYS.map((key) => [key, v[key] ?? null]));
  return { ...rest, ...added } as Answers;
};

// 保存済みの回答を復元する。キーが無い場合・JSONが壊れている場合・形が Answers と一致しない場合は
// null を返し、呼び出し側で INITIAL_ANSWERS にフォールバックさせる。
export const loadSavedAnswers = (): Answers | null => {
  const storage = getSessionStorage();
  if (!storage) return null;
  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isValidAnswersShape(parsed) ? normalizeAnswers(parsed) : null;
  } catch {
    return null;
  }
};

export const saveAnswers = (answers: Answers): void => {
  const storage = getSessionStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(answers));
  } catch {
    // 容量超過等の書き込み失敗は診断の続行を妨げないよう無視する
  }
};

// 診断の送信成功後に呼び出し、次回アクセス時に古い回答が復元されないようにする。
export const clearSavedAnswers = (): void => {
  const storage = getSessionStorage();
  if (!storage) return;
  storage.removeItem(STORAGE_KEY);
};

// 診断の送信成功（409で既に完了済みと判明した場合を含む）後に呼び出す。
// これ以降、同一タブセッション内でフォームのルートへ戻っても isCompleted() で検知できるようにする。
export const markCompleted = (): void => {
  const storage = getSessionStorage();
  if (!storage) return;
  try {
    storage.setItem(COMPLETED_STORAGE_KEY, "1");
  } catch {
    // 書き込みに失敗しても診断の完了自体は成立しているため無視する（最悪、戻ってもフォームが再表示されるだけ）
  }
};

// マウント時の判定用。true ならこのセッション中に既に完了操作をしているため、フォームを表示せず結果画面へ誘導する。
export const isCompleted = (): boolean => {
  const storage = getSessionStorage();
  if (!storage) return false;
  return storage.getItem(COMPLETED_STORAGE_KEY) === "1";
};

// 完了フラグを取り消す。ログアウトを経由しないセッション（例: セッション失効後に別アカウントで再ログイン）で
// このフラグだけが古いまま残ると、結果取得(404)→イントロ→フォーム→isCompleted()で結果画面へ…という
// 無限ループに陥るため、GETが404（=このアカウントには診断結果が無い）と判明した時点で必ず呼び出すこと。
export const clearCompleted = (): void => {
  const storage = getSessionStorage();
  if (!storage) return;
  storage.removeItem(COMPLETED_STORAGE_KEY);
};
